import "./OrdersComponent.scss";
import template from "./OrdersComponent.html";
import {IComponentOptions, IOnDestroy, IOnInit} from "angular";
import {OrderViewModel, OrderViewModels} from "../../models/OrderViewModel";
import {BehaviorSubject, Observable, ReplaySubject, Scheduler} from "rxjs";
import {UserInterfaceConfigurationService} from "../../../services/configuration/UserInterfaceConfigurationService";
import {OrderListColumns} from "../list/models/OrderListColumns";
import {WorkingOrderListOrderBy} from "./models/WorkingOrderListOrderBy";
import {
    completedOrderIncludeUnfilledUpdate,
    completedOrderListColumnsUpdate,
    completedOrderListIncludeExternalUpdate,
    completedOrderOnlyMineUpdate,
    OrderListConfigUpdate,
    workingOrderListColumnsUpdate,
    workingOrderListIncludeExternalUpdate,
    workingOrderListOnlyMineUpdate,
    workingOrderListSortingUpdate
} from "./models/OrderListConfigUpdate";
import {OrderListConfig} from "./models/OrderListConfig";
import {isNotNull} from "../../../js/util/UtilFunctions";
import {applyConfigUpdate} from "./util/OrderConfigChangeUtil";
import {
    buildCompletedOrderListViewModels,
    buildOrderListViewModels,
    completedOrders,
    openOrders,
    sortCompletedOrders,
    sortOpenOrders
} from "./util/OrderListUtil";
import {OrderListViewModels} from "../list/models/OrderListViewModel";
import {DateTimeUtil} from "../../../js/util/DateTimeUtil";
import FileSaver from "file-saver";
import {CurrentUserService} from "../../../services/user/CurrentUserService";
import {OrderListLayout, userOrderListLayout} from "../util/OrderComponentsUtil";

export const TfOrders = "tfOrders";

export function tfOrders(): IComponentOptions {
    return {
        template,
        bindings: {
            orders: "<",
            configKey: "<",
            defaultOrderListConfig: "<"
        },
        controllerAs: "ctrl",
        controller: OrdersComponentController
    };
}

export class OrdersComponentController implements IOnInit, IOnDestroy {

    /** @input */
    public set orders(orders: OrderViewModels | null) {
        if (orders == null) {
            this.orders$.next([]);
        } else {
            this.orders$.next(orders);
        }
    }

    private readonly orders$ = new ReplaySubject<OrderViewModels>(1);

    /** @input */
    public set defaultOrderListConfig(config: OrderListConfig | null) {
        if (config != null) {
            this.defaultOrderListConfig$.next(config);
        }
    }

    private readonly defaultOrderListConfig$ = new ReplaySubject<OrderListConfig>(1);

    /** @input */
    public set configKey(key: string | null) {
        if (key == null || key === "") {
            this.configKey$.next(null);
        } else {
            this.configKey$.next(key);
        }
    }

    private readonly configKey$ = new ReplaySubject<string | null>(1);

    public readonly workingOrderListSorting$: Observable<WorkingOrderListOrderBy>;
    public readonly workingOrders$: Observable<OrderListViewModels>;
    public readonly workingOrderColumns$: Observable<OrderListColumns>;
    public readonly workingOrderListOnlyMine$: Observable<boolean>;
    public readonly workingOrderListIncludeExternal$: Observable<boolean>;

    public readonly completedOrders$: Observable<OrderListViewModels>;
    public readonly completedOrderColumns$: Observable<OrderListColumns>;
    public readonly completedOrdersIncludeUnfilled$: Observable<boolean>;
    public readonly completedOrderListOnlyMine$: Observable<boolean>;
    public readonly completedOrderIncludeExternal$: Observable<boolean>;

    public readonly externalOrdersCount$: Observable<number>;

    public readonly orderSortingOptions = [
        WorkingOrderListOrderBy.ACCOUNT,
        WorkingOrderListOrderBy.ASX_CODE,
        WorkingOrderListOrderBy.CREATED,
        WorkingOrderListOrderBy.LAST_UPDATED,
        WorkingOrderListOrderBy.ORDER_TREE
    ];

    public workingOrdersFilterValue = "";
    public completedOrdersFilterValue = "";

    private readonly orderViewType$: Observable<OrderListLayout>;
    private readonly config$: Observable<OrderListConfig>;
    private readonly configUpdate$ = new ReplaySubject<OrderListConfigUpdate>(1);
    private readonly saveConfig$ = new ReplaySubject<OrderListConfig>(1);
    private readonly workingOrderFilterValue$ = new BehaviorSubject("");
    private readonly completedOrdersFilterValue$ = new BehaviorSubject("");

    private readonly exportActiveOrders$ = new ReplaySubject<boolean>(1);
    private readonly exportCompletedOrders$ = new ReplaySubject<boolean>(1);

    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private readonly userInterfaceConfigurationService: UserInterfaceConfigurationService,
                       private readonly currentUserService: CurrentUserService) {
        this.orderViewType$ = this.buildOrderViewType();
        this.config$ = this.buildConfig();
        this.workingOrderListSorting$ = this.buildWorkingOrdersSorting();
        this.workingOrderColumns$ = this.buildWorkingOrdersColumns();
        this.workingOrders$ = this.buildWorkingOrders();
        this.workingOrderListOnlyMine$ = this.buildWorkingOnlyMine();
        this.workingOrderListIncludeExternal$ = this.buildWorkingIncludeExternal();
        this.completedOrdersIncludeUnfilled$ = this.buildCompletedIncludeUnfilled();
        this.completedOrderColumns$ = this.buildCompletedOrderColumns();
        this.completedOrders$ = this.buildCompletedOrders();
        this.completedOrderListOnlyMine$ = this.buildCompletedOnlyMine();
        this.completedOrderIncludeExternal$ = this.buildCompletedIncludeExternal();
        this.externalOrdersCount$ = this.buildExternalOrdersCount();

        this.watchExportActiveOrders();
        this.watchExportCompletedOrders();
    }

    private buildOrderViewType(): Observable<OrderListLayout> {
        return this.currentUserService
                   .current
                   .takeUntil(this.destroyed$)
                   .map((user) => userOrderListLayout(user))
                   .publishReplay(1)
                   .refCount();
    }

    private buildConfig(): Observable<OrderListConfig> {
        return this.configKey$
                   .filter(isNotNull)
                   .switchMap((key) => this.retrieveConfig(key))
                   .switchMap((config) => this.watchChanges(config))
                   .publishReplay(1)
                   .refCount();
    }

    private retrieveConfig(key: string): Observable<OrderListConfig> {
        const response = this.userInterfaceConfigurationService.load<OrderListConfig>(key);
        const config$ = Observable.fromPromise(response);
        const source$ = Observable.combineLatest(config$, this.defaultOrderListConfig$);
        return source$.map(([config, defaultConfig]) => config ?? defaultConfig)
                      .filter(isNotNull)
                      .catch(() => Observable.empty());
    }

    private buildWorkingOrdersSorting(): Observable<WorkingOrderListOrderBy> {
        return this.config$
                   .map((config) => config.working)
                   .map((working) => working.order)
                   .distinctUntilChanged()
                   .publishReplay(1)
                   .refCount();
    }

    private buildWorkingOrdersColumns(): Observable<OrderListColumns> {
        return this.defaultOrderListConfig$
                   .map((config) => config.working)
                   .filter(isNotNull)
                   .map((working) => working.columns);
    }

    private buildCompletedOrderColumns(): Observable<OrderListColumns> {
        return this.defaultOrderListConfig$
                   .map((config) => config.completed)
                   .filter(isNotNull)
                   .map((working) => working.columns);
    }

    private buildWorkingOrders(): Observable<OrderListViewModels> {
        const orders$ = this.orders$.map(openOrders);
        const mineOnly$ = this.config$
                              .map((c) => c.working)
                              .filter(isNotNull)
                              .map((w) => w.onlyMine)
                              .filter(isNotNull)
                              .startWith(false);
        const includeExternal$ = this.config$
                                     .map((c) => c.working)
                                     .filter(isNotNull)
                                     .map((w) => w.includeExternal)
                                     .filter(isNotNull)
                                     .startWith(false);
        const filterValue$ = this.workingOrderFilterValue$
                                 .debounceTime(500);

        const filteredOrders$ = Observable.combineLatest(orders$, mineOnly$, filterValue$, includeExternal$)
                                          .map(([orders, mineOnly, filter, external]) => {
                                              return buildOrderListViewModels(orders, mineOnly, filter, external);
                                          });

        const source$ = Observable.combineLatest(filteredOrders$, this.workingOrderListSorting$);

        return source$.map(([orders, sorting]) => sortOpenOrders(orders, sorting))
                      .publishReplay(1)
                      .refCount();
    }

    private buildCompletedOrders(): Observable<OrderListViewModels> {
        const orders$ = this.orders$.map(completedOrders);
        const mineOnly$ = this.config$
                              .map((c) => c.completed)
                              .filter(isNotNull)
                              .map((w) => w.onlyMine)
                              .filter(isNotNull)
                              .startWith(false);
        const includeExternal$ = this.config$
                                     .map((c) => c.completed)
                                     .filter(isNotNull)
                                     .map((w) => w.includeExternal)
                                     .filter(isNotNull)
                                     .startWith(false);
        const filterValue$ = this.completedOrdersFilterValue$
                                 .debounceTime(500);

        const source$ = Observable.combineLatest(orders$,
                                                 filterValue$,
                                                 this.completedOrdersIncludeUnfilled$,
                                                 mineOnly$,
                                                 includeExternal$);

        return source$.map(([orders, filter, includeUnfilled, mineOnly, external]) => {
            return buildCompletedOrderListViewModels(orders,
                                                     filter,
                                                     includeUnfilled,
                                                     mineOnly,
                                                     external);
        })
                      .map(sortCompletedOrders)
                      .publishReplay(1)
                      .refCount();
    }

    private buildCompletedIncludeUnfilled(): Observable<boolean> {
        return this.config$
                   .map((config) => config.completed)
                   .map((completed) => completed.includeUnfilled)
                   .distinctUntilChanged()
                   .publishReplay(1)
                   .refCount();
    }

    private buildWorkingOnlyMine(): Observable<boolean> {
        return this.config$
                   .map((config) => config.working)
                   .map((working) => working.onlyMine)
                   .distinctUntilChanged()
                   .filter(isNotNull)
                   .startWith(false)
                   .publishReplay(1)
                   .refCount();
    }

    private buildWorkingIncludeExternal(): Observable<boolean> {
        return this.config$
                   .map((config) => config.working)
                   .map((working) => working.includeExternal)
                   .distinctUntilChanged()
                   .filter(isNotNull)
                   .startWith(false)
                   .publishReplay(1)
                   .refCount();
    }

    private buildCompletedOnlyMine(): Observable<boolean> {
        return this.config$
                   .map((config) => config.completed)
                   .map((working) => working.onlyMine)
                   .distinctUntilChanged()
                   .filter(isNotNull)
                   .startWith(false)
                   .publishReplay(1)
                   .refCount();
    }

    private buildCompletedIncludeExternal() {
        return this.config$
                   .map((config) => config.completed)
                   .map((working) => working.includeExternal)
                   .distinctUntilChanged()
                   .filter(isNotNull)
                   .startWith(false)
                   .publishReplay(1)
                   .refCount();
    }

    public $onInit(): void {
        this.saveConfig$
            .takeUntil(this.destroyed$)
            .observeOn(Scheduler.async)
            .auditTime(500)
            .withLatestFrom(this.configKey$)
            .subscribe(([config, key]) => {
                if (key != null) {
                    this.userInterfaceConfigurationService.save(key, config);
                }
            });
    }

    private watchChanges(initial: OrderListConfig): Observable<OrderListConfig> {
        return this.configUpdate$
                   .scan((acc, next) => applyConfigUpdate(acc, next), initial)
                   .do((config) => this.saveConfig$.next(config))
                   .startWith(initial);
    }

    public updateSorting(value: WorkingOrderListOrderBy) {
        const update = workingOrderListSortingUpdate(value);
        this.configUpdate$.next(update);
    }

    public updateWorkingColumns(columns: OrderListColumns) {
        const update = workingOrderListColumnsUpdate(columns);
        this.configUpdate$.next(update);
    }

    public updateCompletedColumns(columns: OrderListColumns) {
        const update = completedOrderListColumnsUpdate(columns);
        this.configUpdate$.next(update);
    }

    public toggleShowUnfilled() {
        const update = completedOrderIncludeUnfilledUpdate();
        this.configUpdate$.next(update);
    }

    public updateWorkingOrdersFilter(filterValue: string) {
        this.workingOrderFilterValue$.next(filterValue);
    }

    public updateCompletedOrdersFilter(filterValue: string) {
        this.completedOrdersFilterValue$.next(filterValue);
    }

    public toggleWorkingMineOnly() {
        const update = workingOrderListOnlyMineUpdate();
        this.configUpdate$.next(update);
    }

    public toggleCompletedMineOnly() {
        const update = completedOrderOnlyMineUpdate();
        this.configUpdate$.next(update);
    }

    public toggleWorkingIncludeExternal() {
        const update = workingOrderListIncludeExternalUpdate();
        this.configUpdate$.next(update);
    }

    public toggleCompletedIncludeExternal() {
        const update = completedOrderListIncludeExternalUpdate();
        this.configUpdate$.next(update);
    }

    public exportActiveOrders() {
        this.exportActiveOrders$.next(true);
    }

    private buildExternalOrdersCount() {
        return this.orders$
                   .map((orders) => this.countExternalOrders(orders))
                   .publishReplay(1)
                   .refCount();
    }

    private countExternalOrders(orders: OrderViewModels): number {
        return orders.filter((order) => !order.isInternal).length;
    }

    private watchExportActiveOrders() {
        this.exportActiveOrders$
            .takeUntil(this.destroyed$)
            .withLatestFrom(this.workingOrders$)
            .subscribe(([_, orders]) => {
                const today = DateTimeUtil.isoToday();
                const fileName = `active-orders-${today}.csv`;

                this.exportOrders(orders, fileName);
            });
    }

    public exportInactiveOrders() {
        this.exportCompletedOrders$.next(true);
    }

    private watchExportCompletedOrders() {
        this.exportCompletedOrders$
            .takeUntil(this.destroyed$)
            .withLatestFrom(this.completedOrders$)
            .subscribe(([_, orders]) => {
                const today = DateTimeUtil.isoToday();
                const fileName = `inactive-orders-${today}.csv`;

                this.exportOrders(orders, fileName);
            });
    }

    public exportOrders(orders: OrderViewModels, fileName: string) {
        const header = this.orderCsvHeader();
        const body = orders.map((model) => this.formatOrderAsCsv(model))
                           .join("\n");
        const csv = header + "\n" + body;

        const options = {type: "text/csv"};
        const blob = new Blob([csv], options);
        FileSaver.saveAs(blob, fileName);
    }

    private static csvSanitizeString(value: any): string {
        if (value == null) {
            return "";
        } else if (typeof value === "number") {
            return "" + value;
        } else if (typeof value === "boolean") {
            return value ? "true" : "false";
        } else {
            return "\"" + value.replace(/"/g, "\"\"") + "\"";
        }
    }

    private orderCsvHeader(): string {
        const headers = ["order-number", "account-number", "account-name", "code", "exchange", "side", "quantity", "filled", "remaining", "price-instruction", "limit", "average-price", "lifetime", "created", "status", "updated"];

        return headers.map((value) => OrdersComponentController.csvSanitizeString(value))
                      .join(",");
    }

    private formatOrderAsCsv(model: OrderViewModel) {
        const orderNumber = model.orderNumber;
        const accountNumber = model.account?.number;
        const accountName = model.account?.name;
        const code = model.code;
        const exchange = model.exchange;
        const side = model.side;
        const quantity = model.quantity;
        const filled = model.filledQuantity;
        const remaining = model.remainingQuantity;
        const priceInstruction = model.pricingInstruction;
        const limit = model.limit;
        const averagePrice = model.averagePrice;
        const lifetime = model.lifetime;
        const created = model.created;
        const status = model.status;
        const updated = model.updated;

        const values = [orderNumber, accountNumber, accountName, code, exchange, side, quantity, filled, remaining, priceInstruction, limit, averagePrice, lifetime, created, status, updated];

        return values.map((value) => OrdersComponentController.csvSanitizeString(value))
                     .join(",");
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
